/* eslint-disable max-len */
import mixpanel from 'mixpanel-browser'

import useCurrentUser from '@hooks/useCurrentUser'

import { useRelations } from '@hooks/useAgency'

interface DefaultProperties {
  agency: string,
  agencyState: string,
  brand: string,
  brandId?: string,
  isAdmin: boolean,
  isAgencyAdmin: boolean,
  isAgencyUser: boolean,
  isAgent: boolean,
  isBrandUser: boolean,
  isEbuAdmin: boolean,
  isImpersonatingUser: boolean,
  isProviderDirector: boolean,
  isProviderUser: boolean,
  legacy: boolean,
  provider: string,
  role: string,
  userId: number,
  windowLocation: string,
}

const mixpanelToken = process.env.REACT_APP_MIXPANEL_TOKEN
const enableInDevelopment = process.env.REACT_APP_MIXPANEL_ENABLE_IN_DEVELOPMENT === 'true'
const applicationEnv = process.env.REACT_APP_APPLICATION_ENV

const isDevelopment = applicationEnv === 'development'
const isProduction = applicationEnv === 'production'

const mixpanelDefaultConfig = {
  api_transport: 'sendBeacon',
  batch_requests: true,
  debug: !isProduction,
  persistence: 'localStorage',
}

const mixpanelDefaultSuperProperties = {
  Product: 'Realhub',
}

type DispatchMixpanelEventType = {
  customOptions?: Record<string, string>,
  eventType: string,
}

const dispatchMixpanelEvent = (params: DispatchMixpanelEventType) => {
  const {
    customOptions = {}, eventType,
  } = params

  if (!eventType) return null

  return mixpanel.track(eventType, {
    ...customOptions,
  })
}

type MixPanelOptions = {
  shouldAutoCapture?: boolean,
  shouldTrackPageView?: boolean,
}

type InitMixpanelParams = {
  defaultProperties: DefaultProperties,
  shouldAutoCapture?: boolean,
  shouldTrackPageView?: boolean,
}

const initMixpanel = (params: InitMixpanelParams) => {
  if (!mixpanelToken) return null
  if (isDevelopment && !enableInDevelopment) return null

  const { defaultProperties, shouldAutoCapture, shouldTrackPageView } = params
  const { isAdmin, userId } = defaultProperties

  const allowAdvancedTracking = shouldAutoCapture && !isAdmin

  const mergedProperties = {
    ...mixpanelDefaultSuperProperties,
    ...defaultProperties,
  }

  mixpanel.init(
      mixpanelToken!,
      {
        ...mixpanelDefaultConfig,
        // @ts-expect-error TS2353
        autocapture: allowAdvancedTracking ? {
          capture_text_content: true,
          click: true,
          input: true,
          pageview: 'full-url',
          scroll: true,
          submit: true,
        } : false,
        record_sessions_percent: allowAdvancedTracking ? 1 : 0, // As per Mixpanel's recommendation we should start with 1% and move up
        track_pageview: shouldTrackPageView,
        // To ensure that we unmask all but sensitive fields, we added record_mask_text_selector.
        // To mask sensitive data, make to add .mp-mask class to the elements you want to mask.
        record_mask_text_selector: '.mp-mask',
      },
  )

  // identify need to be defined before building people's properties
  mixpanel.identify(`realhub_${userId}`)
  mixpanel.people.set(mergedProperties)

  // Automatically start Session Recording
  if (allowAdvancedTracking){
    mixpanel.start_session_recording()
  }

  return null
}

function useMixpanel(options: MixPanelOptions = {}) {
  const { shouldAutoCapture = true, shouldTrackPageView = true } = options

  const currentUserPayload = useCurrentUser()
  const {
    agency,
    brand,
    currentUser: { id },
    isAdmin,
    isAgencyAdmin,
    isAgencyUser,
    isAgent,
    isBrandUser,
    isEbuAdmin,
    isImpersonatingUser,
    isProviderDirector,
    isProviderUser,
    provider,
    roleTitle,
  } = currentUserPayload

  const agencyRelations = useRelations(agency)
  const { brand: agencyBrand } = agencyRelations

  const defaultProperties: DefaultProperties = {
    agency: agency?.title || 'N/A',
    agencyState: agency?.address_state || 'N/A',
    brand: brand?.title || agencyBrand?.title || 'N/A',
    // @ts-expect-error TS2322
    brandId: brand?.id,
    isAdmin,
    isAgencyAdmin,
    isAgencyUser,
    isAgent,
    isBrandUser,
    isEbuAdmin,
    isImpersonatingUser,
    isProviderDirector,
    isProviderUser,
    legacy: false,
    provider: provider?.title || 'N/A',
    role: roleTitle,
    // @ts-expect-error TS2322
    userId: id,
    windowLocation: window.location.href,
  }

  return {
    callbacks: {
      dispatchMixpanelEvent: (eventType: string, customOptions?: Record<string, any>) => dispatchMixpanelEvent({
        customOptions,
        eventType,
      }),
      initMixpanel: () => initMixpanel({ defaultProperties, shouldAutoCapture, shouldTrackPageView }),
    },
  }
}

export default useMixpanel
